<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

const props = withDefaults(
	defineProps<{
		modelValue: boolean;
		files: FileDTO[];
		folders?: ResourceTreeNodeDO[];
		resourceTreeCategory: "requested_documents" | "dashboards";
	}>(),
	{ folders: () => [] }
);

/** The noun the summary counts in, per category. */
const COUNT_KEYS = {
	requested_documents: "fileBrowser.delete.documentCount",
	dashboards: "fileBrowser.delete.dashboardCount",
};

const { t } = useI18n();

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

/** Absent, not empty, on a dashboard listing - so count it as none. */
const filesUsedOnDashboards = computed(() => {
	return props.files.filter((file) => (file.dashboard_titles?.length ?? 0) > 0);
});

const headers = computed(() => [
	{
		title: t("fileBrowser.delete.batchConfiguration"),
		align: "start" as const,
		key: "title",
		sortable: false,
	},
	{
		title: t("fileBrowser.delete.batchUsedIn"),
		align: "start" as const,
		key: "dashboard_titles",
		sortable: false,
	},
]);

/** "2 documents and 1 folder", with each half counted in its own noun. */
const selectionSummary = computed(() => {
	const files = props.files.length
		? t(COUNT_KEYS[props.resourceTreeCategory], props.files.length)
		: null;
	const folders = props.folders.length
		? t("fileBrowser.delete.folderCount", props.folders.length)
		: null;

	if (files && folders) {
		return t("fileBrowser.delete.summaryJoin", { files, folders });
	}

	return files ?? folders ?? "";
});

const title = computed(() => {
	if (!props.folders.length) {
		return t("fileBrowser.delete.batchFilesTitle");
	}

	return props.files.length
		? t("fileBrowser.delete.batchItemsTitle")
		: t("fileBrowser.delete.batchFoldersTitle");
});

const cancel = () => {
	emit("cancel");
	emit("update:modelValue", false);
};

const confirm = () => {
	emit("confirm");
	emit("update:modelValue", false);
};

const onDialogUpdate = (value: boolean) => {
	if (!value) {
		cancel();
	}
};
</script>

<template>
	<v-dialog
		:model-value="modelValue"
		max-width="640"
		@update:model-value="onDialogUpdate"
	>
		<v-card elevation="9">
			<v-toolbar flat>
				<v-toolbar-title>{{ title }}</v-toolbar-title>
			</v-toolbar>
			<v-divider />

			<v-card-text>
				<template v-if="folders.length">
					<p>{{ t("fileBrowser.delete.batchFolderNote", folders.length) }}</p>
					<div class="mt-2">
						<v-chip
							v-for="folder in folders"
							:key="folder.id"
							size="small"
							density="comfortable"
							color="primary"
							variant="tonal"
							class="mr-1 mb-1"
						>
							{{ folder.name }}
						</v-chip>
					</div>
					<p
						v-if="resourceTreeCategory === 'requested_documents'"
						class="mt-3 text-medium-emphasis"
					>
						{{ t("fileBrowser.delete.batchDocumentsNote") }}
					</p>
				</template>

				<template v-if="filesUsedOnDashboards.length">
					<p :class="folders.length ? 'mt-4' : ''">
						{{ t("fileBrowser.delete.batchDashboardsNote") }}
					</p>
					<v-data-table
						density="compact"
						hide-default-footer
						class="mt-3"
						:items="filesUsedOnDashboards"
						:headers="headers"
					>
						<template v-slot:item.dashboard_titles="{ item }">
							<v-chip
								v-for="dashboardTitle in item.dashboard_titles"
								:key="dashboardTitle"
								size="small"
								density="comfortable"
								color="primary"
								variant="tonal"
								class="mr-1 mb-1"
							>
								{{ dashboardTitle }}
							</v-chip>
						</template>
					</v-data-table>
				</template>

				<p
					:class="{
						'mt-3': folders.length || filesUsedOnDashboards.length,
					}"
				>
					{{
						t("fileBrowser.delete.batchConfirm", { summary: selectionSummary })
					}}
				</p>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-btn color="primary" @click="cancel">
					{{ t("fileBrowser.actions.cancel") }}
				</v-btn>
				<v-btn variant="tonal" color="red" @click="confirm">
					{{ t("fileBrowser.actions.delete") }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
